import { useEffect, useState } from "react";
import { AdvanceSearchWrapper } from "../../ManagementPortal/Shared";
import { QICustomSelect } from "../../../components";
import { getLocalizedString } from "../../../shared/translation";
import {
  useGetEventConfigurationsQuery,
  useGetEventTypesQuery,
  useGetUsersByRoleQuery,
  useGetVehiclesQuery,
  useGetDevicesQuery,
} from "../../ManagementPortal/services";
import { FMDPStorage } from "../../../shared/helper";
import { eventsType, getUserIdForFiltering } from "../utils";

const uniqueOptions = (values, selected) =>
  [...new Set([...values, selected].filter(Boolean).map(String))].sort((a, b) => a.localeCompare(b));
const searchParams = (key, value) => ({
  per_page: 50,
  ...(value ? { q: JSON.stringify({ [key]: value }) } : {}),
});

export const EventsAdvanceSearch = ({ onSearch, activeSearch, onClose, events = [] }) => {
  const initialValues = {
    plate_number_cont: "",
    event_name_cont: "",
    event_type_cont: "",
    event_sub_type_cont: "",
    device_name_cont: "",
    device_imei_cont: "",
    driver_name_cont: "",
  };

  const [formData, setFormData] = useState(initialValues);
  const [searchTerms, setSearchTerms] = useState({});
  const currentUser = FMDPStorage.get("current-user");
  const selectedAppId = FMDPStorage.get("selected-app-id");

  const { data: vehicles } = useGetVehiclesQuery({
    ...searchParams("plate_number_cont", searchTerms.plate_number_cont),
    filter_user_id: getUserIdForFiltering(currentUser, selectedAppId),
  });
  const { data: eventConfigurations } = useGetEventConfigurationsQuery(
    searchParams("name_cont", searchTerms.event_name_cont)
  );
  const { data: eventTypes } = useGetEventTypesQuery({ per_page: 100 });
  const { data: devicesByName } = useGetDevicesQuery(
    searchParams("name_cont", searchTerms.device_name_cont)
  );
  const { data: devicesByImei } = useGetDevicesQuery(
    searchParams("device_id_cont", searchTerms.device_imei_cont)
  );
  const { data: drivers } = useGetUsersByRoleQuery({
    ...searchParams("filter_full_name_or_email_cont", searchTerms.driver_name_cont),
    role: "driver",
  });

  const options = {
    plate_number_cont: uniqueOptions(
      [...(vehicles?.data || []).map((vehicle) => vehicle.plate_number), ...events.map((event) => event.plate_number)],
      formData.plate_number_cont
    ),
    event_name_cont: uniqueOptions(
      [
        ...(eventConfigurations?.data || []).map((configuration) => configuration.name),
        ...events.map((event) => event.events?.config?.name || event.events?.config?.Config?.name),
      ],
      formData.event_name_cont
    ),
    event_type_cont: uniqueOptions(
      [...(eventTypes?.data || []).map((type) => type.name), ...events.map((event) => event.events?.type)],
      formData.event_type_cont
    ),
    // ponytail: Subtypes are limited to loaded events; add a lookup API to cover unseen pages.
    event_sub_type_cont: uniqueOptions(
      events.map((event) => event.events?.sub_type),
      formData.event_sub_type_cont
    ),
    device_name_cont: uniqueOptions(
      [
        ...(devicesByName?.data || []).map((device) => device.name),
        ...events.map((event) => event.events?.device_name),
      ],
      formData.device_name_cont
    ),
    device_imei_cont: uniqueOptions(
      [
        ...(devicesByImei?.data || []).map((device) => device.device_id),
        ...events.map((event) => event.source_id),
      ],
      formData.device_imei_cont
    ),
    driver_name_cont: uniqueOptions(
      [
        ...(drivers?.data || []).map((driver) => `${driver.first_name || ""} ${driver.last_name || ""}`.trim()),
        ...events.map((event) => event.events?.driver_name),
      ],
      formData.driver_name_cont
    ),
  };

  useEffect(() => {
    setFormData(activeSearch || initialValues);
  }, [activeSearch]);

  const handleFormChange = (key, value) => {
    setFormData((prevState) => ({ ...prevState, [key]: value }));
  };

  const clearAdvanceSearch = () => {
    setFormData(initialValues);
    onSearch(null);
  };

  const handleSearch = () => {
    // Filter out empty values
    const filteredData = Object.keys(formData).reduce((acc, key) => {
      if (formData[key] && formData[key].toString().trim() !== "") {
        acc[key] = formData[key];
      }
      return acc;
    }, {});

    onSearch(Object.keys(filteredData).length > 0 ? filteredData : null);

    onClose();
  };

  return (
    <div className="events-advance-search">
      <AdvanceSearchWrapper
        activeFilters={activeSearch}
        onClear={clearAdvanceSearch}
        onSearch={handleSearch}
        defaultOpen={true}
        showSearchText={false}
        showCollapsible={false}
        disableClickOutside
      >
        {[
          ["plate_number_cont", "plate_number", "Plate Number"],
          ["event_name_cont", "event_name", "Event Name"],
          ["event_type_cont", "event_type", "Event Type"],
          ["event_sub_type_cont", "event_sub_type", "Event Sub Type"],
          ["device_name_cont", "device_name", "Device Name"],
          ["device_imei_cont", "device_imei", "Device IMEI"],
          ["driver_name_cont", "driver_name", "Driver Name"],
        ].map(([key, translationKey, label]) => (
          <QICustomSelect
            key={key}
            label={getLocalizedString(translationKey, label)}
            value={formData[key]}
            onChange={(value) => handleFormChange(key, value)}
            onSearch={(value) => setSearchTerms((terms) => ({ ...terms, [key]: value }))}
            onSearchPlaceHolder={getLocalizedString("search", "Search")}
            autoHeightMax="140px"
          >
            <li value="">{getLocalizedString("select", "Select")}</li>
            {options[key]
              .filter((value) =>
                !searchTerms[key] ||
                value.toLowerCase().includes(searchTerms[key].toLowerCase()) ||
                ((key === "event_type_cont" || key === "event_sub_type_cont") &&
                  eventsType(value).toLowerCase().includes(searchTerms[key].toLowerCase())) ||
                value === formData[key]
              )
              .map((value) => (
                <li value={value} key={value}>
                  {key === "event_type_cont" || key === "event_sub_type_cont"
                    ? eventsType(value)
                    : value}
                </li>
              ))}
          </QICustomSelect>
        ))}
      </AdvanceSearchWrapper>
    </div>
  );
};
